/*
* An abstract class. All inheriting classes should implement:
*   updateIsLoadingState() : a function which updates the current state.isLoading state
* */

const React = require('react');

import API from "./api";
import Constants from "./Constants";
import Session from "./session";

const MAX_RETRIES = 5;

class BasicPageBody extends React.Component {

    constructor(props){
        super(props);

        // set the school name
        const urlSplitted = window.location.href.split("/");
        this.schoolName = urlSplitted[3]; // The school name place
        this.retries = 0; // Counts the number of times we retried a call
    }

    reportSentAPIRequest(a) {
      const sentOn = (new Date()).getTime();
      const currentSent = [...(this.state.sentAPITimes || []), sentOn];
      this.setState({sentAPITimes: currentSent});
      return sentOn;
    }

    removeResponsesFromAPIWaitingList() {
      API.removeFromWaitingList(this.state.sentAPITimes);
    }

    /**
     * General function for fetcing resources from the api
     * @param stateKey the key in the state in which the response should be stored.
     *          if the stateKey is undefined, then no state will be changed.
     * @param failMsg the message to present on fail
     * @param apiFunc the API function. e.g. API.getSchoolObj
     * @param options is an object, with the following fields:
     *          @param shouldUpdateLoadingState determines whther we should update the
     *                  component's loading state
     *          @param onSuccess is optional. called on success. receives the response
     *          @param onAuthError is optional. called instead of notifying on 401/403
     *          @param apiParams is optional. it's an array of extra api call parameters
     */
    loadResource(stateKey, failMsg, apiFunc, options, tryNum) {
        // Default params
        if(typeof options.onSuccess === 'undefined')
            options.onSuccess = () => {};

        if(typeof options.apiParams === 'undefined')
            options.apiParams = [];

        var handler = function(resp) {
            // if an error occurred
            if (resp == undefined || (resp.responseCode != 200 && resp.responseCode != 304)) {
                // The caller may want to handle an unusable session itself
                if (options.onAuthError && resp &&
                    (resp.responseCode == 401 || resp.responseCode == 403)) {
                    options.onAuthError(resp);
                    return;
                }

                // Check for 4XX errors - Show specific message and do not retry
                if (resp && resp.responseCode >= 400 && resp.responseCode < 500) {
                    const errorMsg = API.getErrorMessage(resp, failMsg);
                    this.setNotification("bad", errorMsg);
                    return;
                }

                // Check for giving up
                if(this.retries == MAX_RETRIES) {
                    this.setNotification("bad", Constants.fetch.GIVING_UP_MSG);
                    return;
                }

                // Not giving up yet. Retry...
                this.setNotification("bad", failMsg);

                this.retries++;
                setTimeout(() =>
                    apiFunc(this.schoolName, handler.bind(this), ...options.apiParams, this.reportSentAPIRequest()),
                    Constants.TIME_UNTIL_FETCH_RETRY
                );
            } else {
                if(stateKey)
                    this.setState({[stateKey]: resp.responseBody}, () => {
                        if(options.shouldUpdateLoadingState)
                            this.updateIsLoadingState();
                        options.onSuccess(resp.responseBody);
                    });
                else
                    options.onSuccess(resp.responseBody);
            }
        };

        this.retries = 1;
        apiFunc(this.schoolName, handler.bind(this), ...options.apiParams, this.reportSentAPIRequest());
    }

    // This function displays a new notification, and schedules to hide it after
    //  some interval
    // @param duration how long to leave it up. Defaults to Constants.NOTIFICATION_DELAY
    setNotification(temperature, msg, duration) {
        // add the notification
        this.setState({notification: {temperature: temperature, message: msg}});

        // Schedule to remove it in NOTIFICATION_DELAY ms.
        // We pass the message so that the remove function will know
        //  if a new notification was pushed in the time between this call
        //  and the removing function's execution. If it did, it will not remove.
        setTimeout(() => {
                this.eraseNotification(msg);
            },
            duration || Constants.NOTIFICATION_DELAY
        );
    }

    /**
     * Shows the explanation left behind by a page that sent us here. Longer lived than
     *  usual: it arrives with a page load, so there is a new screen to take in first.
     */
    showAccessDeniedNotificationIfAny() {
        const message = Session.denialMessage(Session.takeDenialReason());
        if (message)
            this.setNotification("bad", message, Constants.LONG_NOTIFICATION_DELAY);
    }

    // removes the notification
    eraseNotification(msgOfRemovingNotif) {
        // if the notification we are about to remove is the one that
        //  was called to remove (see explanation in the setNotification method)
        if (this.state.notification !== undefined &&
            this.state.notification.message === msgOfRemovingNotif)
            this.setState({notification: undefined})
    }

    componentDidMount() {
      if(this.props.updateParentIsInMainPage)
        this.props.updateParentIsInMainPage();
    }
    componentWillUnmount() {
      if(this.props.updateParentIsInMainPage)
        this.props.updateParentIsInMainPage();
    }

    doesHaveAdminPriv(){ return Session.hasAdminPriv(); }

    // Redirect using react router. Requires the props to have a updateParentIsInMainPage field
    redirectToMainAdminPage() {
        if (!this.doesHaveAdminPriv()) {
            // Relative, so it keeps the current scheme. The old form was `host + uri`,
            //  which has no scheme and no leading slash, so the browser read it as a
            //  relative path and appended it to the current one.
            window.location.assign(Session.homePathFor(Session.getRole(), this.schoolName));
            return;
        }

        this.removeResponsesFromAPIWaitingList();
        const newURI = "/" + this.schoolName + "/" + Session.pages.ADMIN;

        this.props.history.push(newURI);
        this.props.updateParentIsInMainPage();
    }
}

export default BasicPageBody;
